// GeneralTable.jsx
import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import "./GeneralTable.scss";
import { Icon } from "@iconify/react/dist/iconify.js";

const GeneralTable = ({
  columns,
  data,
  title = "Data Table",
  selectAll = null,
  onSelectAll = null,
  initialPageSize = 50,
  showPagination = true,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // Page size options
  const pageSizeOptions = [10, 25, 50, 100, "All"];

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return data;
    const lowercasedSearchTerm = searchTerm.toLowerCase();

    return data.filter((row) => {
      // Prioritize searching the 'searchableText' field if it exists.
      if (row.searchableText) {
        return row.searchableText.toLowerCase().includes(lowercasedSearchTerm);
      }

      // Fallback for data without a 'searchableText' field.
      return Object.values(row).some((value) => {
        if (typeof value === "string" || typeof value === "number") {
          return String(value).toLowerCase().includes(lowercasedSearchTerm);
        }
        return false;
      });
    });
  }, [data, searchTerm]); // Removed 'columns' from dependencies as it's no longer needed for filtering

  // Calculate pagination
  const totalPages = Math.ceil(filteredData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentData = filteredData.slice(startIndex, endIndex);

  // Reset current page when search changes or page size changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (e) => {
    const value = e.target.value;

    if (value === "All") {
      setPageSize(filteredData.length || data.length);
    } else {
      setPageSize(Number(value));
    }

    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1);
  };

  // Pagination handlers
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalPages);
  const goToPreviousPage = () =>
    setCurrentPage((prev) => Math.max(1, prev - 1));
  const goToNextPage = () =>
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));

  // Generate pagination buttons
  const getPaginationButtons = () => {
    const buttons = [];
    const maxButtons = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxButtons - 1);

    if (endPage - startPage + 1 < maxButtons) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(i);
    }

    return buttons;
  };

  return (
    <div className="general-table-wrapper">
      {/* Dynamic Header */}
      <div className="table-heading-column">
        <div className="table-header">
          <div className="table-title-wrapper">
            <h2 className="table-title">{title}</h2>

            {selectAll !== null && onSelectAll && (
              <div className="select-all-section">
                {React.cloneElement(selectAll, {
                  onChange: onSelectAll,
                })}
              </div>
            )}
          </div>
        </div>

        {/* Search Bar and Controls */}
        <div className="search-bar-container">
          <div className="search-meta-controls">
            <div className="table-controls">
              <div className="search-input-wrapper">
                <Icon icon="ion:search-outline" className="search-icon" />
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                {searchTerm && (
                  <button className="clear-search-btn" onClick={clearSearch}>
                    ✕
                  </button>
                )}
              </div>
            </div>

            {showPagination && (
              <div className="page-size-selector">
                <select
                  id="page-size"
                  value={
                    pageSize === filteredData.length || pageSize === data.length
                      ? "All"
                      : pageSize
                  }
                  onChange={handlePageSizeChange}
                  className="page-size-select"
                >
                  {pageSizeOptions.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {searchTerm && (
            <div className="search-results-info">
              {filteredData.length === 0 ? (
                <span className="no-results">
                  No results found for "{searchTerm}"
                </span>
              ) : (
                <span className="results-count">
                  Found {filteredData.length} result
                  {filteredData.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Table Container with Scrolling */}
      <div className="table-container">
        <table className="general-table">
          <thead className="table-head">
            <tr>
              {columns.map((column, index) => (
                <th
                  key={column.header + index}
                  className={`table-header-cell ${
                    index === 0 ? "first-column" : ""
                  } ${column.hidden ? "hidden-column" : ""}`}
                  style={{
                    minWidth: index === 0 ? "150px" : "100px",
                    display: column.hidden ? "none" : undefined, // 👈 Add this
                  }}
                >
                  <div className="header-content">
                    <span className="header-text">{column.header}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="table-body">
            {currentData.length > 0 ? (
              currentData.map((row, rowIndex) => (
                <tr key={startIndex + rowIndex} className="table-row">
                  {columns.map((column, colIndex) => (
                    <td
                      key={`${startIndex + rowIndex}-${column.accessor}`}
                      className={`table-cell ${
                        colIndex === 0 ? "first-column" : ""
                      } ${column.hidden ? "hidden-column" : ""}`}
                      style={{
                        minWidth: colIndex === 0 ? "100px" : "50px",
                        display: column.hidden ? "none" : undefined,
                      }}
                    >
                      {row[column.accessor] || "—"}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length}>
                  <div
                    className="flex flex-col items-center justify-center text-gray-500 py-10"
                    style={{ minHeight: "200px" }}
                  >
                    <Icon
                      icon="fluent-color:calendar-data-bar-28"
                      className="text-5xl mb-2 w-[50px] h-[50px]"
                    />
                    <p className="text-xl">No data available</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {showPagination && filteredData.length > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            <span className="pagination-text">
              Showing {startIndex + 1} to{" "}
              {Math.min(endIndex, filteredData.length)} of {filteredData.length}{" "}
              entries
            </span>
          </div>

          {totalPages > 1 && (
            <div className="pagination-controls">
              <button
                className="pagination-btn pagination-btn-nav"
                onClick={goToFirstPage}
                disabled={currentPage === 1}
                aria-label="First page"
              >
                <Icon
                  icon="ion:chevron-back-outline"
                  className="pagination-icon"
                />
                <Icon
                  icon="ion:chevron-back-outline"
                  className="pagination-icon"
                />
              </button>

              <button
                className="pagination-btn pagination-btn-nav"
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                aria-label="Previous page"
              >
                <Icon
                  icon="ion:chevron-back-outline"
                  className="pagination-icon"
                />
              </button>

              {getPaginationButtons().map((page) => (
                <button
                  key={page}
                  className={`pagination-btn pagination-btn-number ${
                    page === currentPage ? "active" : ""
                  }`}
                  onClick={() => goToPage(page)}
                  aria-label={`Go to page ${page}`}
                >
                  {page}
                </button>
              ))}

              <button
                className="pagination-btn pagination-btn-nav"
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                aria-label="Next page"
              >
                <Icon
                  icon="ion:chevron-forward-outline"
                  className="pagination-icon"
                />
              </button>

              <button
                className="pagination-btn pagination-btn-nav"
                onClick={goToLastPage}
                disabled={currentPage === totalPages}
                aria-label="Last page"
              >
                <Icon
                  icon="ion:chevron-forward-outline"
                  className="pagination-icon"
                />
                <Icon
                  icon="ion:chevron-forward-outline"
                  className="pagination-icon"
                />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

GeneralTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.string.isRequired,
      accessor: PropTypes.string.isRequired,
    }),
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  title: PropTypes.string,
  selectAll: PropTypes.element,
  onSelectAll: PropTypes.func,
  selectedCount: PropTypes.number,
  initialPageSize: PropTypes.number,
  showPagination: PropTypes.bool,
};

export default GeneralTable;
